import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/client';

interface ICPJob {
  id: string;
  company_name: string;
  campaign_name: string;
  status: 'classifying' | 'review' | 'verdict_ready' | 'error';
  total_records: number;
  classified_count: number;
  review_count: number;
  error_message: string | null;
  created_at: string;
}

const STATUS_META: Record<ICPJob['status'], { label: string; className: string }> = {
  classifying:    { label: 'Classifying…', className: 'bg-blue-100 text-blue-700' },
  review:         { label: 'Needs Review', className: 'bg-amber-100 text-amber-700' },
  verdict_ready:  { label: 'Verdict Ready', className: 'bg-green-100 text-green-700' },
  error:          { label: 'Error', className: 'bg-red-100 text-red-700' },
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const CSRICPSignalList: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<ICPJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadJobs();
  }, []);

  async function loadJobs() {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('icp_jobs')
      .select('id, company_name, campaign_name, status, total_records, classified_count, review_count, error_message, created_at')
      .order('created_at', { ascending: false });

    if (err) {
      setError(err.message);
    } else {
      setJobs((data ?? []) as ICPJob[]);
    }
    setLoading(false);
  }

  function handleRowClick(job: ICPJob) {
    if (job.status === 'review' || job.status === 'classifying') {
      navigate(`/csr-icp-signal/${job.id}/review`);
    } else {
      navigate(`/csr-icp-signal/${job.id}/verdict`);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🔬 ICP Signal Classifier</h1>
            <p className="text-sm text-gray-500 mt-1">
              Upload a campaign's contact list and get AI-powered intent and ICP-fit signals.
            </p>
          </div>
          <button
            onClick={() => navigate('/csr-icp-signal/new')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            + New Analysis
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-16 text-gray-400">
            <div className="text-3xl mb-2">⏳</div>
            <p>Loading analyses…</p>
          </div>
        )}

        {/* Empty */}
        {!loading && jobs.length === 0 && (
          <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
            <div className="text-4xl mb-3">🔬</div>
            <h2 className="text-lg font-semibold text-gray-700 mb-1">No analyses yet</h2>
            <p className="text-sm text-gray-500 mb-4">
              Upload a campaign CSV to start classifying ICP signals.
            </p>
            <button
              onClick={() => navigate('/csr-icp-signal/new')}
              className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700"
            >
              Start First Analysis
            </button>
          </div>
        )}

        {/* Table */}
        {!loading && jobs.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company / Campaign</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Review Queue</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {jobs.map(job => {
                  const meta = STATUS_META[job.status] ?? STATUS_META.error;
                  const pct  = job.total_records > 0
                    ? Math.round((job.classified_count / job.total_records) * 100)
                    : 0;
                  return (
                    <tr
                      key={job.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleRowClick(job)}
                    >
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{job.company_name}</div>
                        <div className="text-xs text-gray-500">{job.campaign_name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${meta.className}`}>
                          {meta.label}
                        </span>
                        {job.status === 'error' && job.error_message && (
                          <div className="text-xs text-red-500 mt-0.5 max-w-xs truncate" title={job.error_message}>
                            {job.error_message}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-600 mb-1">
                          {job.classified_count} / {job.total_records}
                        </div>
                        <div className="w-24 bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-indigo-500 h-1.5 rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {job.review_count > 0
                          ? <span className="text-amber-600 font-medium">{job.review_count} flagged</span>
                          : <span className="text-gray-400">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(job.created_at)}</td>
                      <td className="px-4 py-3 text-sm">
                        {job.status === 'review' && (
                          <span className="text-amber-600 font-medium">Review →</span>
                        )}
                        {job.status === 'verdict_ready' && (
                          <span className="text-green-600 font-medium">View Verdict →</span>
                        )}
                        {job.status === 'classifying' && (
                          <span className="text-blue-500">In progress…</span>
                        )}
                        {job.status === 'error' && (
                          <span className="text-red-500">Error</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSRICPSignalList;
